import { useEffect, useState } from 'react';
import { getSetting } from '@/api';

export type AgreementType = 'service' | 'privacy';

/** 弹层类型 -> 后台配置 key（setting 接口参数） */
const AGREEMENT_KEYS = {
  service: 'serviceagreement',
  privacy: 'privacy',
} as const;

/** 弹层标题 */
const AGREEMENT_TITLES: Record<AgreementType, string> = {
  service: '用户服务协议',
  privacy: '隐私政策',
};

/** 内容缓存（模块级）：登录/注册页共享，同一内容整个会话只请求一次 */
const contentCache: Record<AgreementType, string> = { service: '', privacy: '' };

interface AgreementModalProps {
  /** null 表示关闭 */
  type: AgreementType | null;
  onClose: () => void;
  /** 加载失败回调（页面级 toast 提示） */
  onError?: (msg: string) => void;
}

/**
 * 协议/隐私政策底部弹出层
 * 打开时按类型请求 setting 接口拿后台富文本内容（已有缓存则不重复请求）
 */
export default function AgreementModal({ type, onClose, onError }: AgreementModalProps) {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');

  useEffect(() => {
    if (!type) return;

    //已缓存直接用，不重复请求
    if (contentCache[type]) {
      setContent(contentCache[type]);
      return;
    }

    let mounted = true;
    setLoading(true);
    getSetting(AGREEMENT_KEYS[type])
      .then((res) => {
        contentCache[type] = res.setting?.content || '';
        if (mounted) setContent(contentCache[type]);
      })
      .catch((err) => {
        onError?.(err instanceof Error ? err.message : '加载失败');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
    // onError 由父组件用 useCallback 稳定传入或忽略，仅按 type 触发请求
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  if (!type) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal panel */}
      <div className="relative z-10 bg-white rounded-t-3xl max-h-[60%] flex flex-col shadow-2xl animate-slide-up">
        {/* Modal header */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-background-200/50 shrink-0">
          <div className="w-8 h-8" />
          <h3 className="text-foreground-950 text-sm font-bold">{AGREEMENT_TITLES[type]}</h3>
          <div className="w-8 h-8" />
        </div>

        {/* Modal content */}
        <div className="flex-1 overflow-y-auto px-5 py-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-foreground-400 text-sm">
              <i className="ri-loader-4-line text-2xl animate-spin text-primary-500" />
              加载中...
            </div>
          ) : content ? (
            <div
              className="text-foreground-700 text-sm leading-relaxed [&_p]:mb-3 [&_img]:max-w-full [&_img]:h-auto"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          ) : (
            <div className="text-center text-foreground-400 text-sm py-16">暂无内容</div>
          )}
        </div>

        {/* Modal bottom button */}
        <div className="px-5 pb-6 pt-3 border-t border-background-200/50">
          <button
            onClick={onClose}
            className="w-full h-12 rounded-xl bg-primary-500 text-white text-sm font-bold cursor-pointer hover:bg-primary-600 active:scale-[0.98] transition-all"
          >
            我已阅读并同意
          </button>
        </div>
      </div>
    </div>
  );
}
